import type {
  DocumentTree,
  TreeScheduleRow,
} from '../dto/proposal-document.dto.js'
import { formatMoneyValue, parseMoney, priceMinutes } from './money.js'

/*
 * One tree as a proposal's schedule prices it.
 *
 * Shared so the survey screen can show what a tree and a bid come to while
 * the technician is still standing there, from the same rows the proposal is
 * built from. The row is what `priceSelection` reads, so a figure built any
 * other way would be a second answer to what a tree costs.
 */

/** Add exact-decimal amounts, or null when none of them priced. */
export function sumAmounts(amounts: readonly (string | null)[]): string | null {
  let total = 0
  let priced = false
  for (const amount of amounts) {
    const cents = parseMoney(amount)
    if (cents === null) continue
    total += cents
    priced = true
  }
  return priced ? formatMoneyValue(total) : null
}

/**
 * What a problem costs on one tree: the price the arborist set by hand, or
 * the sum of its treatments. Null when neither priced anything.
 */
export function problemAmount(
  problem: Pick<DocumentTree['problems'][number], 'priceOverride' | 'solutions'>,
): string | null {
  return (
    problem.priceOverride ??
    sumAmounts(problem.solutions.map((solution) => solution.amount))
  )
}

/**
 * Removal as the schedule carries it, priced at the crew rate.
 *
 * Takes `null` for "not offered" rather than assuming the key is present: a
 * document frozen before removal existed has no such field, and the schema
 * defaults it only on the way back in.
 */
export function toScheduleRemoval(
  removal: {
    minutes: number | null
    notes: string | null
    priceOverride?: string | null | undefined
  } | null,
  centsPerHour: number | null,
): {
  minutes: number | null
  notes: string | null
  amount: string | null
} | null {
  if (removal === null) return null
  return {
    minutes: removal.minutes,
    notes: removal.notes,
    // A price the arborist set by hand stands in for the crew rate.
    amount:
      removal.priceOverride ??
      (centsPerHour === null || removal.minutes === null
        ? null
        : formatMoneyValue(priceMinutes(removal.minutes, centsPerHour))),
  }
}

/** A later season's part of one tree, as Future work estimates it. */
export interface FutureTreeWork {
  /** Whether the tree is pruned in that season. */
  prunes: boolean
  /** The pruning's figure: the price set by hand, or its time at the rate. */
  pruningAmount: string | null
  removal: ReturnType<typeof toScheduleRemoval>
  /** What the tree comes to in that season. Null when nothing priced. */
  amount: string | null
}

/**
 * What a tree's work in a later season comes to.
 *
 * Not `priceSelection`'s figure, and deliberately: nobody chooses anything in
 * Future work, so there is no prune-or-remove choice to default. A tree only
 * quoted for removal is a removal, since that is what the arborist would do
 * instead of treating it too; otherwise it is its pruning and its problems.
 */
export function futureTreeWork(
  tree: DocumentTree,
  centsPerHour: number | null,
): FutureTreeWork {
  // A season's part of a tree that is not pruned then carries no jobs and
  // zero minutes, so pruning is jobs, time somebody estimated, or a price
  // somebody set.
  const handPrice = tree.pruningPriceOverride ?? null
  const prunes =
    tree.noPruningRecommended !== true &&
    (tree.jobs.length > 0 ||
      (tree.estimatedLaborMinutes ?? 0) > 0 ||
      handPrice !== null)
  const pruningAmount =
    handPrice ??
    (centsPerHour === null || tree.estimatedLaborMinutes === null
      ? null
      : formatMoneyValue(priceMinutes(tree.estimatedLaborMinutes, centsPerHour)))
  const removal = toScheduleRemoval(tree.removal ?? null, centsPerHour)
  return {
    prunes,
    pruningAmount,
    removal,
    amount:
      !prunes && removal !== null
        ? removal.amount
        : sumAmounts([
            prunes ? pruningAmount : null,
            ...tree.problems.map(problemAmount),
          ]),
  }
}

/** Where a tree falls, for a proposal priced by area. */
export interface ScheduleRowArea {
  areaId: string | null
  areaName: string | null
}

/**
 * A tree as the schedule lists it and the price reads it.
 *
 * Removal is priced at the crew rate like the pruning it competes with, and
 * each problem carries what treating it costs on this tree, by name, so a
 * client declining "Mistletoe" declines the same thing in every block.
 */
export function toTreeScheduleRow(
  tree: DocumentTree,
  centsPerHour: number | null,
  area: ScheduleRowArea = { areaId: null, areaName: null },
): TreeScheduleRow {
  return {
    treeId: tree.treeId ?? null,
    label: tree.label,
    species: tree.species,
    jobs: tree.jobs.map((job) => ({
      type: job.type,
      level: job.level,
      minutes: job.minutes,
    })),
    estimatedLaborMinutes: tree.estimatedLaborMinutes,
    noPruningRecommended: tree.noPruningRecommended ?? false,
    pruningPriceOverride: tree.pruningPriceOverride ?? null,
    areaId: area.areaId,
    areaName: area.areaName,
    // On the row because the plans read the schedule: optional work sits
    // outside "Everything recommended".
    isOptional: tree.isOptional,
    removal: toScheduleRemoval(tree.removal ?? null, centsPerHour),
    problems: tree.problems.map((problem) => ({
      name: problem.name,
      amount: problemAmount(problem),
      // Carried so a plan can take the hazards first, from the document as
      // sent rather than from a re-surveyed tree.
      severity: problem.severity,
    })),
  }
}
